import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";

/**
 * 管理者マイレージ編集ページ（/console/mileage）用 API スライス。
 *
 * - 一覧: `GET /api/admin/mileage-ranks`（MASTERADMIN のみ、分布比率込み）
 * - 作成: `POST /api/admin/mileage-ranks`（multipart、画像対応）
 * - 更新: `PATCH /api/admin/mileage-ranks/<id>`（multipart、楽観ロック）
 * - 削除: `DELETE /api/admin/mileage-ranks/<id>`
 *
 * 全件でも最大 8 件のため、書き込み成功時は一覧タグを無効化して再取得する。
 * FormData を body に渡すと fetchBaseQuery が Content-Type を自動設定する
 * （手動で multipart/form-data を指定しない）。
 */

/** `GET /api/admin/mileage-ranks` の 1 行 */
export interface AdminMileageRank {
  mileage_id: string;
  rank_name_jp: string;
  rank_name_en: string;
  /** ボーダーライン（マイル） */
  point: number;
  /** 利用料率（%） */
  usage_fee: number;
  /** カード画像 URL */
  image: string | null;
  /** デフォルトランク（ボーダーライン変更・削除不可） */
  is_default: boolean;
  created: string;
  modified: string;
  /** 楽観ロック用（更新時に modified として送り返す） */
  modified_timestamp: number;
  /** このランクに属するアーティスト数 */
  artist_count: number;
  /** ランク分布比率（%） */
  ratio_percent: number;
}

export interface AdminMileageRanksData {
  ranks: AdminMileageRank[];
  artist_total: number;
  max_ranks: number;
  min_ranks: number;
}

export interface AdminMileageRanksResponse {
  status: string;
  data: AdminMileageRanksData;
}

export interface AdminMileageRankResponse {
  status: string;
  data: { rank: AdminMileageRank };
}

/** 作成・更新のフォーム値（画像は変更時のみ含める） */
export interface MileageRankInput {
  rank_name_jp?: string;
  rank_name_en?: string;
  point?: number;
  usage_fee?: number;
  image?: File | null;
  /** 楽観ロック: 取得時の modified_timestamp */
  modified?: number;
}

const toFormData = (input: MileageRankInput): FormData => {
  const formData = new FormData();
  if (input.rank_name_jp !== undefined)
    formData.append("rank_name_jp", input.rank_name_jp);
  if (input.rank_name_en !== undefined)
    formData.append("rank_name_en", input.rank_name_en);
  if (input.point !== undefined) formData.append("point", String(input.point));
  if (input.usage_fee !== undefined)
    formData.append("usage_fee", String(input.usage_fee));
  if (input.image) formData.append("image", input.image);
  if (input.modified !== undefined)
    formData.append("modified", String(input.modified));
  return formData;
};

export const adminMileageRanksApi = createApi({
  reducerPath: "adminMileageRanksApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["AdminMileageRanks"],
  endpoints: (builder) => ({
    /** 全ランク一覧（-point 順、分布比率込み） */
    getAdminMileageRanks: builder.query<AdminMileageRanksResponse, void>({
      query: () => "/api/admin/mileage-ranks",
      providesTags: ["AdminMileageRanks"],
    }),

    /** ランク作成（multipart） */
    createAdminMileageRank: builder.mutation<
      AdminMileageRankResponse,
      MileageRankInput
    >({
      query: (input) => ({
        url: "/api/admin/mileage-ranks",
        method: "POST",
        body: toFormData(input),
      }),
      invalidatesTags: ["AdminMileageRanks"],
    }),

    /** ランク更新（multipart、楽観ロック付き） */
    updateAdminMileageRank: builder.mutation<
      AdminMileageRankResponse,
      { mileageId: string; input: MileageRankInput }
    >({
      query: ({ mileageId, input }) => ({
        url: `/api/admin/mileage-ranks/${mileageId}`,
        method: "PATCH",
        body: toFormData(input),
      }),
      invalidatesTags: ["AdminMileageRanks"],
    }),

    /** ランク削除 */
    deleteAdminMileageRank: builder.mutation<
      { status: string; data: { mileage_id: string } },
      string
    >({
      query: (mileageId) => ({
        url: `/api/admin/mileage-ranks/${mileageId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["AdminMileageRanks"],
    }),
  }),
});

export const {
  useGetAdminMileageRanksQuery,
  useCreateAdminMileageRankMutation,
  useUpdateAdminMileageRankMutation,
  useDeleteAdminMileageRankMutation,
} = adminMileageRanksApi;
